import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Hammer, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatCurrency } from "@/lib/api";
import { workCategoryOf, WORK_TYPES, staleScopeDays } from "@/pages/Deals";

/** One strip, four lanes — jump between every scope-building tool from any of them. */
export const SCOPE_LANES = [
  { key: "fluid", label: "Fluid Applied Roofing", to: "/calculator", desc: "FARM · Silicone · Acrylic" },
  { key: "traditional", label: "Traditional Commercial", to: "/single-ply", desc: "TPO · EPDM · PVC · ModBit" },
  { key: "shingle", label: "Shingle Roofing", to: "/deals?new=shingle", desc: "Asphalt shingle proposals" },
  { key: "construction", label: "Construction / Remodel", to: "/deals?new=construction", desc: "Repairs & non-roofing work" },
];

export function ScopeLaneSwitcher({ active }) {
  return (
    <div data-testid="scope-lane-switcher">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">
        Build a Scope — pick your work type
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {SCOPE_LANES.map((lane) => {
          const isActive = lane.key === active;
          return (
            <Link
              key={lane.key}
              to={lane.to}
              data-testid={`scope-lane-${lane.key}`}
              className={`block text-left px-3 py-2.5 border-2 rounded-sm transition-colors ${
                isActive ? "border-blue-700 bg-blue-50 pointer-events-none" : "border-zinc-200 bg-white hover:border-zinc-400"
              }`}
            >
              <div className={`text-xs font-black uppercase tracking-wide ${isActive ? "text-blue-800" : "text-zinc-800"}`}>
                {lane.label}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">{lane.desc}</div>
            </Link>
          );
        })}
      </div>
      <div className="mt-1.5 text-[10px] text-zinc-500">
        Multi-supplier job costing (Home Depot / Lowe's / subs)?{" "}
        <Link to="/custom-estimates" data-testid="scope-lane-custom-estimates" className="inline-flex items-center gap-1 font-bold text-blue-700 hover:underline">
          <Hammer className="w-3 h-3" /> Custom Estimates
        </Link>
      </div>
    </div>
  );
}

const LANE_CHIP = {
  fluid: "bg-blue-100 text-blue-800",
  traditional: "bg-zinc-200 text-zinc-800",
  shingle: "bg-amber-100 text-amber-800",
  construction: "bg-emerald-100 text-emerald-800",
};

const estOf = (d) => {
  const chosen = +d.chosen_amount || 0;
  if (chosen) return { v: chosen, est: false };
  const opts = [d.proposal_option_25yr, d.proposal_option_1, d.proposal_option_2, d.proposal_option_3]
    .map(Number).filter((v) => v > 0).sort((a, b) => a - b);
  return { v: opts.length ? opts[Math.floor((opts.length - 1) / 2)] : 0, est: true };
};

const resumeTo = (d, cat) => {
  if (cat === "fluid") return `/calculator?deal=${d.id}`;
  if (cat === "traditional") return `/deals/${d.id}`;
  return `/deals?edit=${d.id}`;
};

/** "Pick up where you left off" — the most recently touched active scopes. */
export function RecentScopes() {
  const [rows, setRows] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const load = () =>
    api.get("/deals")
      .then((r) => setRows((r.data || []).filter((d) => !/won|lost|archived|past/i.test(d.status || "")).slice(0, 6)))
      .catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const sendProposal = async (d) => {
    if (!window.confirm(`Email the scope PDF for "${d.title}" to the project's primary contact and mark it Sent?`)) return;
    setSendingId(d.id);
    try {
      const r = await api.post(`/deals/${d.id}/spec-sheet/email`, { mark_sent: true });
      toast.success(r.data?.message || "Proposal sent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSendingId(null);
    }
  };
  if (!rows || rows.length === 0) return null;
  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid="recent-scopes">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-100">
        <Clock className="w-3.5 h-3.5 text-zinc-500" />
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
          Pick Up Where You Left Off — Recent Scopes
        </div>
      </div>
      <div className="divide-y divide-zinc-100">
        {rows.map((d) => {
          const cat = workCategoryOf(d.proposed_roof_type || "");
          const lane = WORK_TYPES.find((w) => w.key === cat);
          const { v, est } = estOf(d);
          const updated = (d.updated_at || d.created_at || "").slice(0, 10);
          return (
            <div key={d.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`recent-scope-${d.id}`}>
              <Link to={`/deals/${d.id}`} className="font-bold text-sm text-zinc-900 hover:text-blue-700 truncate flex-1 min-w-0">
                {d.title}
              </Link>
              <span className={`px-2 py-0.5 rounded-sm text-[9px] font-black uppercase tracking-wide shrink-0 ${LANE_CHIP[cat]}`}>
                {lane?.label || cat}
              </span>
              {staleScopeDays(d) > 0 && (
                <>
                  <span
                    className="px-2 py-0.5 rounded-sm text-[9px] font-black uppercase tracking-wide shrink-0 bg-amber-100 text-amber-800"
                    title={`Scope created ${staleScopeDays(d)} days ago and still not sent — send the proposal or follow up.`}
                    data-testid={`stale-scope-chip-${d.id}`}
                  >
                    Not sent · {staleScopeDays(d)}d
                  </span>
                  <button
                    type="button"
                    data-testid={`hub-send-proposal-${d.id}`}
                    disabled={sendingId === d.id}
                    onClick={() => sendProposal(d)}
                    className="shrink-0 px-2 py-0.5 rounded-sm text-[9px] font-black uppercase tracking-wide border border-amber-700 text-amber-800 bg-white hover:bg-amber-50 disabled:opacity-50"
                    title="One-click: email the scope PDF to the primary contact and mark this deal Sent."
                  >
                    {sendingId === d.id ? "Sending…" : "Send"}
                  </button>
                </>
              )}
              <span className="text-xs font-mono font-bold text-zinc-700 w-28 text-right shrink-0">
                {v > 0 ? `${formatCurrency(v)}${est ? " est." : ""}` : "—"}
              </span>
              <span className="text-[10px] text-zinc-400 w-20 text-right shrink-0">{updated}</span>
              <Link
                to={resumeTo(d, cat)}
                data-testid={`resume-scope-${d.id}`}
                className="shrink-0 px-2.5 h-7 inline-flex items-center text-[10px] font-bold uppercase tracking-wider border border-blue-700 text-blue-700 hover:bg-blue-50 rounded-sm"
              >
                Resume
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}
